import React, { createContext, useState, useContext } from 'react';
import { supabase } from '../services/supabaseClient';
import {
  deriveWrappingKey,
  generateMasterKey,
  wrapMasterKey,
  unwrapMasterKey,
  AAD_MK_PASSWORD_V1,
  deriveFromSeed,
  wrapWithSeed,
  unwrapWithSeed,
} from '../services/cryptoService';

const CryptoContext = createContext();

export const useCrypto = () => {
  const context = useContext(CryptoContext);
  if (!context) throw new Error('useCrypto must be used within a CryptoProvider');
  return context;
};

export const CryptoProvider = ({ children }) => {
  const [masterKey, setMasterKey] = useState(null);

  const isUnlocked = masterKey !== null;

  // Crée la MK, la scelle DEUX FOIS (wrap password + wrap seed) et écrit
  // les 7 colonnes crypto atomiquement (DEC-030, DEC-031).
  // Appelée par l'UI onboarding après affichage + confirmation retape des
  // 3 mots : seed_confirmed_at est horodaté dans le même UPDATE, l'appel
  // n'ayant lieu qu'une fois la seed confirmée.
  const initializeMKWithSeed = async (password, userId, seedPhrase) => {
    // Garde non-écrasement : un profil déjà scellé ne doit JAMAIS être
    // ré-initialisé. Un second appel écraserait encrypted_mk / encrypted_mk_seed
    // et rendrait le coffre définitivement illisible (MK d'origine perdue).
    const { data: existing, error: guardError } = await supabase
      .from('user_profiles')
      .select('encrypted_mk, encrypted_mk_seed')
      .eq('id', userId)
      .single();

    if (guardError) {
      throw new Error('Impossible de vérifier le profil : ' + guardError.message);
    }
    if (existing?.encrypted_mk || existing?.encrypted_mk_seed) {
      throw new Error('Coffre déjà initialisé. Réinitialisation refusée.');
    }

    const mkBytes = generateMasterKey();

    // Deux dérivations en parallèle (indépendantes).
    const [passwordDerivation, seedDerivation] = await Promise.all([
      deriveWrappingKey(password),
      deriveFromSeed(seedPhrase),
    ]);
    const { key: passwordKey, salt: mkSalt } = passwordDerivation;
    const { key: seedKey, saltBase64: mkSeedSalt } = seedDerivation;

    // Deux wraps en parallèle (IV distincts CSPRNG par appel).
    const [passwordWrap, seedWrap] = await Promise.all([
      wrapMasterKey(mkBytes, passwordKey, AAD_MK_PASSWORD_V1(userId)),
      wrapWithSeed(mkBytes, seedKey, userId),
    ]);

    // UPDATE atomique 7 colonnes + verrou .select().single().
    const { data: updated, error } = await supabase
      .from('user_profiles')
      .update({
        encrypted_mk: passwordWrap.ciphertext,
        mk_iv: passwordWrap.iv,
        mk_salt: mkSalt,
        encrypted_mk_seed: seedWrap.ciphertext,
        mk_seed_iv: seedWrap.iv,
        mk_seed_salt: mkSeedSalt,
        seed_confirmed_at: new Date().toISOString(),
      })
      .eq('id', userId)
      .select('id')
      .single();

    if (error) {
      throw new Error('Impossible de sauvegarder la clé chiffrée : ' + error.message);
    }
    if (!updated) {
      throw new Error(`Aucun profil user_profiles trouvé pour id=${userId}`);
    }

    const mkKey = await crypto.subtle.importKey(
      'raw',
      mkBytes,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
    setMasterKey(mkKey);
    return mkKey;
  };

  // Déverrouille via mot de passe. SELECT élargi aux 6 colonnes crypto
  // pour anticiper les états hybrides post-CP3.
  // La création de MK ne passe plus par ici (chemin auto-init K13 retiré en
  // CP4) : elle est le fait exclusif de l'onboarding, qui appelle
  // initializeMKWithSeed après affichage et confirmation de la seed BIP39.
  const unlockVault = async (password, userId) => {
    const { data: profile, error } = await supabase
      .from('user_profiles')
      .select('encrypted_mk, mk_iv, mk_salt, encrypted_mk_seed, mk_seed_iv, mk_seed_salt')
      .eq('id', userId)
      .single();

    if (error) throw new Error('Impossible de récupérer le profil : ' + error.message);

    // Aucune MK scellée : le compte n'a pas terminé son onboarding. On refuse
    // explicitement plutôt que de créer une MK ici (DEC-030 : la seed doit
    // avoir été affichée et confirmée avant tout scellement).
    if (!profile?.encrypted_mk) {
      throw new Error('Compte non initialisé. Onboarding requis.');
    }

    const { key: wrappingKey } = await deriveWrappingKey(password, profile.mk_salt);
    const aad = AAD_MK_PASSWORD_V1(userId);

    let mkKey;
    try {
      mkKey = await unwrapMasterKey(profile.encrypted_mk, profile.mk_iv, wrappingKey, aad);
    } catch {
      throw new Error('Mot de passe incorrect ou données corrompues.');
    }

    setMasterKey(mkKey);
    return mkKey;
  };

  // Déverrouille via seed BIP39 (fallback recovery, DEC-030).
  // Sera consommé par CP6 (Forgot password) après updateUser du mot de passe.
  const unlockWithSeed = async (seedPhrase, userId) => {
    const { data: profile, error } = await supabase
      .from('user_profiles')
      .select('encrypted_mk_seed, mk_seed_iv, mk_seed_salt')
      .eq('id', userId)
      .single();

    if (error) throw new Error('Impossible de récupérer le profil : ' + error.message);

    if (!profile?.encrypted_mk_seed) {
      throw new Error('Compte sans seed de récupération (legacy pré-PF-01).');
    }

    const { key: seedKey } = await deriveFromSeed(seedPhrase, profile.mk_seed_salt);

    let mkKey;
    try {
      mkKey = await unwrapWithSeed(
        profile.encrypted_mk_seed,
        profile.mk_seed_iv,
        seedKey,
        userId
      );
    } catch {
      throw new Error('Seed incorrecte ou données corrompues.');
    }

    setMasterKey(mkKey);
    return mkKey;
  };

  // Détruit la MK en mémoire (logout ou navigation protégée — DEC-016).
  const lockVault = () => setMasterKey(null);

  return (
    <CryptoContext.Provider value={{
      masterKey,
      isUnlocked,
      unlockVault,
      unlockWithSeed,
      initializeMKWithSeed,
      lockVault,
    }}>
      {children}
    </CryptoContext.Provider>
  );
};

export default CryptoContext;
